import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';

import { pipe } from 'fp-ts/lib/function';
import * as E from 'fp-ts/Either';
import { parseUrl } from './utils/parseUrl';
import { isNil } from './utils/isNil';

const isBlank = (val: any) => isNil(val) || val === '';

/**
 * @deprecated "Use `required2` and set a message"
 * @param val
 */
export const required = (val: string | number | undefined) =>
  !isBlank(val) ? undefined : "Can't be blank";

export const required2 =
  (message: string = "Can't be blank") =>
  (val: string | number | undefined) =>
    !isBlank(val) ? undefined : message;

export const date = (val: string | undefined) => {
  if (isNil(val) || val === '') {
    return undefined;
  }

  return isValid(parseISO(val)) ? undefined : 'Enter a valid date';
};

// Mostly RFC 5322 compliant taken from http://emailregex.com/
export function emailAddress(
  value: string | typeof undefined,
): typeof undefined | string {
  if (!value) {
    return undefined;
  }
  const emailPattern =
    /^[a-z0-9\u007F-\uffff!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9\u007F-\uffff!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;

  return emailPattern.exec(value) ? undefined : 'Enter a valid email address';
}

export const time24hr = (val: string | undefined) => {
  if (!val) {
    return undefined;
  }

  // validates 24-hour time in format HH:MM. Leading zero required.
  return /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(val)
    ? undefined
    : 'Enter a valid time';
};

export const oneOf =
  (
    options: string[],
    message: string = `Must be one of "${options.join('", "')}"`,
  ) =>
  (val: string | undefined) => {
    return val && options.indexOf(val) < 0 ? message : undefined;
  };

export const maxLength =
  (max: number, message: string = `Must be shorter than ${max} characters`) =>
  (val: string | undefined) => {
    return val && val.length > max ? message : undefined;
  };

export const minLength =
  (min: number, message: string = `Enter at least ${min} characters`) =>
  (val: string | undefined) => {
    return val && val.length < min ? message : undefined;
  };

export const maxFileSize =
  (maxMB: number, message: string = `Must no larger than ${maxMB}MB`) =>
  (file: File | undefined) => {
    return file && file.size > maxMB * 1024 * 1024 ? message : undefined;
  };

export const imageFileTypes = ['image/jpeg', 'image/png', 'image/gif'];
export const fileType =
  (allowedTypes: string[], message: string = 'File must be a valid type') =>
  (file: File | undefined) => {
    return file && !allowedTypes.includes(file.type) ? message : undefined;
  };

export const isValidPaymentURL = (value: string): string | undefined => {
  if (isBlank(value)) {
    return undefined;
  }

  const supportedBankHostnames = [
    'monzo.me',
    'settleup.starlingbank.com',
    'paypal.me',
  ];

  return pipe(
    value,
    parseUrl,
    E.filterOrElse(
      (url) => supportedBankHostnames.includes(url.hostname),
      () => 'unsupported bank',
    ),
    E.filterOrElse(
      ({ pathname }) => !isBlank(pathname) && pathname !== '/',
      () => 'no pathname',
    ),
    E.fold(
      () => 'Enter a supported payment link (Monzo, PayPal, or Starling).',
      () => undefined,
    ),
  );
};

export const isValidURL = (value: string): string | undefined => {
  if (isBlank(value)) {
    return undefined;
  }

  return pipe(
    value,
    parseUrl,
    // ensure the hostname looks like a real URL
    E.filterOrElse(
      (url) => url.host.split('.').filter((p) => !!p).length > 1,
      () => 'invalid hostname',
    ),
    E.fold(
      () => 'Enter a valid link',
      () => undefined,
    ),
  );
};

export const validateInteger = (value: any) => {
  if (!isNaN(value) && Math.floor(value) === value) {
    return undefined;
  }

  return 'Enter a valid number';
};

export const minPasswordLength = minLength(
  6,
  'Password must be at least 6 characters',
);
